import { apiClient, handleApiSuccess } from './api';

export interface ClubPost {
  id: string;
  authorId: string;
  authorName: string;
  authorRole: string;
  authorProfilePicture?: string;
  club: {
    id: string;
    name: string;
    logo: string;
  };
  content: string;
  images?: string[];
  image?: string; // backward compatibility
  likes: number;
  comments: Array<{
    id: string;
    authorId: string;
    authorName: string;
    content: string;
    images?: string[];
    timestamp: string;
  }>;
  timestamp: string;
  isLiked: boolean;
  isPinned?: boolean;
}

export interface CreateClubPostRequest {
  content: string;
  images?: FileList;
}

export interface GetClubPostsResponse {
  data: ClubPost[];
  pagination: {
    page: number;
    limit: number;
    total: number;
  };
}

// Internal backend response format (before handleApiSuccess processing)
interface BackendClubPostsResponse {
  data: ClubPost[];
  pagination: {
    page: number;
    limit: number;
    total: number;
  };
}

/**
 * Club Posts Service
 * Handles all club posts (insider feed) related API calls
 */
export class ClubPostService {
  /**
   * Get club posts with pagination (insider feed)
   */
  static async getClubPosts(clubId: string, page: number = 1, limit: number = 10): Promise<GetClubPostsResponse> {
    const response = await apiClient.get(`/club-posts/${clubId}?page=${page}&limit=${limit}`);

    // Check if response.data has the expected structure
    if (response.success && response.data) {
      // Backend sends: {success: true, data: {data: [...], pagination: {...}}}
      // OR: {success: true, data: [...], pagination: {...}}
      if (Array.isArray(response.data)) {
        // Case 1: data is directly the posts array, pagination is at root level
        return {
          data: response.data as ClubPost[],
          pagination: (response as any).pagination || {
            page: page,
            limit: limit,
            total: response.data.length
          }
        };
      } else if (response.data.data && response.data.pagination) {
        // Case 2: data contains both data and pagination
        return {
          data: response.data.data as ClubPost[],
          pagination: response.data.pagination
        };
      }
    }

    // Fallback: try handleApiSuccess
    return handleApiSuccess(response);
  }

  /**
   * Create a new club post
   */
  static async createClubPost(clubId: string, data: CreateClubPostRequest): Promise<ClubPost> {
    const formData = new FormData();
    formData.append('content', data.content);
    formData.append('clubId', clubId);

    if (data.images && data.images.length > 0) {
      Array.from(data.images).forEach((file) => {
        formData.append('images', file);
      });
    }

    const response = await apiClient.post('/club-posts', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return handleApiSuccess(response);
  }

  /**
   * Like/unlike a club post
   */
  static async toggleLike(postId: string): Promise<{ isLiked: boolean; likesCount: number }> {
    const response = await apiClient.post(`/club-posts/${postId}/like`);
    return handleApiSuccess(response);
  }

  /**
   * Add comment to a club post
   */
  static async addComment(postId: string, content: string, images?: FileList): Promise<void> {
    const formData = new FormData();
    formData.append('content', content);

    if (images && images.length > 0) {
      Array.from(images).forEach((file) => {
        formData.append('images', file);
      });
    }

    const response = await apiClient.post(`/club-posts/${postId}/comment`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return handleApiSuccess(response);
  }

  /**
   * Delete a club post
   */
  static async deletePost(postId: string): Promise<void> {
    const response = await apiClient.delete(`/club-posts/${postId}`);
    return handleApiSuccess(response);
  }

  /**
   * Pin/unpin a club post (Club admin only)
   */
  static async togglePin(postId: string): Promise<{ isPinned: boolean }> {
    const response = await apiClient.post(`/club-posts/${postId}/pin`);
    return handleApiSuccess(response);
  }
}

export default ClubPostService;